import { useCallback } from 'react';
import { useMappedState } from 'redux-react-hook';

import { featureFlagsApi, IFeatureFlag, useGetFeatureFlagsQuery } from '../services/api/featureFlagsApi';
import { RootState } from '../store';

interface QueryState {
  data?: IFeatureFlag[];
  isLoading?: boolean;
}

export const useFeatureFlag = (flagName: string): boolean | null => {
  const { isLoading } = useGetFeatureFlagsQuery();

  const mapState = useCallback(
    (state: RootState) => {
      const queries = state[featureFlagsApi.reducerPath]?.queries || {};
      const entry = Object.values(queries).find(
        (q: unknown) => {
          const query = q as QueryState;
          return Array.isArray(query?.data) && query?.data[0]?.name && query?.data[0]?.isActive !== undefined;
        }
      );
      return {
        featureFlags: entry?.data || []
      };
    },
    []
  );

  const { featureFlags } = useMappedState(mapState);

  if (isLoading) {
    return null;
  }

  const flag = Array.isArray(featureFlags)
    ? featureFlags.find((f: IFeatureFlag) => f.name === flagName)
    : undefined;
  return flag?.isActive || false;
};

export const useFeatureFlags = (): { [key: string]: boolean } => {
  const mapState = useCallback(
    (state: RootState) => {
      const queries = state[featureFlagsApi.reducerPath]?.queries || {};
      const entry = Object.values(queries).find(
        (q: unknown) => {
          const query = q as QueryState;
          return Array.isArray(query?.data) && query?.data[0]?.name && query?.data[0]?.isActive !== undefined;
        }
      );
      return {
        featureFlags: entry?.data || []
      };
    },
    []
  );

  const { featureFlags } = useMappedState(mapState);

  return Array.isArray(featureFlags)
    ? featureFlags.reduce((acc: { [key: string]: boolean }, flag: IFeatureFlag) => ({
      ...acc,
      [flag.name]: flag.isActive
    }), {})
    : {};
}; 